import type { KnowledgeRun } from '../../compass/models/KnowledgeIngestion'
import type {
  KnowledgeGraphRecord,
  PersistedGraph,
  PersistedNode,
} from '../../compass/models/KnowledgeGraph'
import {
  NETWORK_REVIEW_BASELINE_METADATA_KEY,
  NETWORK_REVIEW_BASELINE_VERSION,
  type NetworkReviewBaseline,
  type NetworkReviewBaselineChangeSummary,
  type NetworkReviewBaselineResourceFingerprint,
} from '../models/ReviewBaseline'

const MAX_DIFF_KEYS = 25

const RESOURCE_ID_KEYS = [
  'arn',
  'resourceId',
  'providerResourceId',
  'cloudResourceId',
  'assetId',
  'id',
  'uid',
  'name',
] as const

const PROVIDER_KEYS = [
  'hostingProvider',
  'cloudProvider',
  'provider',
  'platform',
] as const

const ACCOUNT_KEYS = [
  'accountId',
  'subscriptionId',
  'projectId',
  'tenantId',
  'resourceGroup',
] as const

const REGION_KEYS = [
  'region',
  'location',
  'zone',
  'availabilityZone',
] as const

const FINGERPRINT_PROP_KEYS = [
  'accountId',
  'application',
  'arn',
  'cloudProvider',
  'complianceType',
  'costCenter',
  'costCentre',
  'createdOutsideIac',
  'driftStatus',
  'encryptedAtRest',
  'hostingProvider',
  'iacManaged',
  'lifecycleState',
  'managedBy',
  'managedService',
  'manual',
  'monthlyCost',
  'monthlyCostUsd',
  'monthlySpend',
  'owner',
  'platform',
  'projectId',
  'provider',
  'providerResourceId',
  'publicExposure',
  'region',
  'resourceGroup',
  'resourceId',
  'resourceType',
  'serviceOwner',
  'state',
  'status',
  'subscriptionId',
  'team',
  'tenantId',
  'utilization',
  'utilizationPct',
  'workload',
] as const

export function readNetworkReviewBaselineFromMetadata(
  metadata: Record<string, unknown> | null | undefined,
): NetworkReviewBaseline | null {
  return parseNetworkReviewBaseline(metadata?.[NETWORK_REVIEW_BASELINE_METADATA_KEY])
}

export function parseNetworkReviewBaseline(value: unknown): NetworkReviewBaseline | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const raw = value as Record<string, unknown>
  const id = stringValue(raw.id)
  const graphId = stringValue(raw.graphId)
  const acceptedAt = stringValue(raw.acceptedAt)
  const graphVersion = numberValue(raw.graphVersion)
  const graphUpdatedAt = stringValue(raw.graphUpdatedAt)
  const rawResources = raw.resources
  if (
    !id ||
    !graphId ||
    !acceptedAt ||
    graphVersion == null ||
    !graphUpdatedAt ||
    !rawResources ||
    typeof rawResources !== 'object' ||
    Array.isArray(rawResources)
  ) {
    return null
  }

  const resources: Record<string, NetworkReviewBaselineResourceFingerprint> = {}
  for (const [key, resource] of Object.entries(rawResources)) {
    const parsed = parseResourceFingerprint(key, resource)
    if (parsed) resources[parsed.key] = parsed
  }

  return {
    id,
    version: NETWORK_REVIEW_BASELINE_VERSION,
    acceptedAt,
    acceptedBy: nullableStringValue(raw.acceptedBy),
    graphId,
    graphVersion,
    graphUpdatedAt,
    sourceRunIds: arrayOfStrings(raw.sourceRunIds),
    sourceCount: numberValue(raw.sourceCount) ?? 0,
    resourceCount: numberValue(raw.resourceCount) ?? Object.keys(resources).length,
    resources,
  }
}

export function createNetworkReviewBaseline(input: {
  graphRecord: KnowledgeGraphRecord
  runs: readonly KnowledgeRun[]
  acceptedBy?: string | null
  now?: string
}): NetworkReviewBaseline {
  const now = input.now ?? new Date().toISOString()
  const resources = fingerprintGraphResources(input.graphRecord.graph)
  const sourceRunIds = sourceRunIdsForBaseline(input.runs)
  return {
    id: `baseline-${input.graphRecord.id}-v${input.graphRecord.version}-${Date.parse(now) || Date.now()}`,
    version: NETWORK_REVIEW_BASELINE_VERSION,
    acceptedAt: now,
    acceptedBy: input.acceptedBy ?? null,
    graphId: input.graphRecord.id,
    graphVersion: input.graphRecord.version,
    graphUpdatedAt: dateLikeToIso(input.graphRecord.updatedAt),
    sourceRunIds,
    sourceCount: sourceRunIds.length,
    resourceCount: Object.keys(resources).length,
    resources,
  }
}

export function summarizeNetworkReviewBaseline(input: {
  baseline: NetworkReviewBaseline | null
  graphRecord: KnowledgeGraphRecord
  runs: readonly KnowledgeRun[]
}): NetworkReviewBaselineChangeSummary {
  const current = fingerprintGraphResources(input.graphRecord.graph)
  const currentKeys = Object.keys(current).sort()
  const currentRunIds = sourceRunIdsForBaseline(input.runs)

  if (!input.baseline) {
    return {
      hasBaseline: false,
      baselineId: null,
      acceptedAt: null,
      acceptedBy: null,
      graphId: input.graphRecord.id,
      baselineGraphVersion: null,
      currentGraphVersion: input.graphRecord.version,
      graphVersionDelta: null,
      baselineResourceCount: 0,
      currentResourceCount: currentKeys.length,
      addedCount: 0,
      removedCount: 0,
      changedCount: 0,
      unchangedCount: currentKeys.length,
      currentRunIds,
      newRunIds: currentRunIds,
      changedResourceKeys: [],
      addedResourceKeys: [],
      removedResourceKeys: [],
    }
  }

  const baselineKeys = Object.keys(input.baseline.resources).sort()
  const baselineKeySet = new Set(baselineKeys)
  const currentKeySet = new Set(currentKeys)
  const addedResourceKeys = currentKeys.filter((key) => !baselineKeySet.has(key))
  const removedResourceKeys = baselineKeys.filter((key) => !currentKeySet.has(key))
  const changedResourceKeys = currentKeys.filter((key) => {
    const baselineResource = input.baseline?.resources[key]
    return baselineResource && baselineResource.fingerprint !== current[key]?.fingerprint
  })
  const unchangedCount = currentKeys.filter((key) => {
    const baselineResource = input.baseline?.resources[key]
    return baselineResource && baselineResource.fingerprint === current[key]?.fingerprint
  }).length

  const baselineRunIds = new Set(input.baseline.sourceRunIds)
  const newRunIds = currentRunIds.filter((id) => !baselineRunIds.has(id))

  return {
    hasBaseline: true,
    baselineId: input.baseline.id,
    acceptedAt: input.baseline.acceptedAt,
    acceptedBy: input.baseline.acceptedBy,
    graphId: input.graphRecord.id,
    baselineGraphVersion: input.baseline.graphVersion,
    currentGraphVersion: input.graphRecord.version,
    graphVersionDelta: input.graphRecord.version - input.baseline.graphVersion,
    baselineResourceCount: input.baseline.resourceCount,
    currentResourceCount: currentKeys.length,
    addedCount: addedResourceKeys.length,
    removedCount: removedResourceKeys.length,
    changedCount: changedResourceKeys.length,
    unchangedCount,
    currentRunIds,
    newRunIds,
    changedResourceKeys: changedResourceKeys.slice(0, MAX_DIFF_KEYS),
    addedResourceKeys: addedResourceKeys.slice(0, MAX_DIFF_KEYS),
    removedResourceKeys: removedResourceKeys.slice(0, MAX_DIFF_KEYS),
  }
}

export function fingerprintGraphResources(
  graph: Pick<PersistedGraph, 'nodes'>,
): Record<string, NetworkReviewBaselineResourceFingerprint> {
  const resources: Record<string, NetworkReviewBaselineResourceFingerprint> = {}
  const seen = new Map<string, number>()
  for (const node of graph.nodes) {
    if (!isReviewableResource(node)) continue
    const baseKey = resourceIdentityKey(node)
    const duplicateCount = seen.get(baseKey) ?? 0
    seen.set(baseKey, duplicateCount + 1)
    const key = duplicateCount === 0 ? baseKey : `${baseKey}|node:${normalizeKeyPart(node.id)}`
    resources[key] = resourceFingerprint(node, key)
  }
  return resources
}

function resourceFingerprint(
  node: PersistedNode,
  key: string,
): NetworkReviewBaselineResourceFingerprint {
  const props = propsOf(node)
  const provider = stringProp(props, PROVIDER_KEYS)
  const accountId = stringProp(props, ACCOUNT_KEYS)
  const region = stringProp(props, REGION_KEYS)
  const resourceType = resourceTypeOf(node)
  const comparable = {
    type: node.type,
    label: node.label ?? null,
    tags: normalizedTags(props.tags),
    props: pickComparableProps(props),
  }
  return {
    key,
    nodeId: node.id,
    label: displayNodeLabel(node),
    type: node.type,
    provider,
    accountId,
    region,
    resourceType,
    fingerprint: hashString(stableStringify(comparable)),
  }
}

function resourceIdentityKey(node: PersistedNode): string {
  const props = propsOf(node)
  const provider = stringProp(props, PROVIDER_KEYS)
  const accountId = stringProp(props, ACCOUNT_KEYS)
  const region = stringProp(props, REGION_KEYS)
  const resourceType = resourceTypeOf(node)
  const resourceId =
    stringProp(props, RESOURCE_ID_KEYS) ??
    node.label ??
    node.id
  return [
    provider ?? 'provider',
    accountId ?? 'account',
    region ?? 'global',
    resourceType ?? node.type ?? 'resource',
    resourceId,
  ].map(normalizeKeyPart).join('|')
}

function isReviewableResource(node: PersistedNode): boolean {
  const props = propsOf(node)
  const concreteResourceType = concreteResourceTypeOf(node)
  const resourceType = (concreteResourceType ?? node.type ?? '').toLowerCase()
  if (['account', 'region', 'geographic_region', 'availability_zone'].includes(resourceType)) {
    return false
  }
  if (stringProp(props, RESOURCE_ID_KEYS) || concreteResourceType) return true
  if (['artifact', 'data-store', 'actor', 'resource-group'].includes(node.type)) return true
  if (node.type === 'policy' || node.type === 'place') {
    return Boolean(
      stringProp(props, PROVIDER_KEYS) ||
      stringProp(props, ACCOUNT_KEYS) ||
      stringProp(props, REGION_KEYS),
    )
  }
  return false
}

function resourceTypeOf(node: PersistedNode): string | null {
  return concreteResourceTypeOf(node) ?? node.type
}

function concreteResourceTypeOf(node: PersistedNode): string | null {
  const props = propsOf(node)
  return stringProp(props, ['resourceType', 'managedService', '_manifestId', 'manifestId'])
}

function displayNodeLabel(node: PersistedNode): string {
  const props = propsOf(node)
  return stringProp(props, ['displayName', 'name', 'label', 'title', 'resourceId']) ??
    node.label ??
    node.id
}

function pickComparableProps(props: Record<string, unknown>): Record<string, unknown> {
  const picked: Record<string, unknown> = {}
  for (const key of FINGERPRINT_PROP_KEYS) {
    if (props[key] !== undefined) picked[key] = normalizeComparableValue(props[key])
  }
  const access = props.access
  if (access && typeof access === 'object' && !Array.isArray(access)) {
    picked.access = normalizeComparableValue(access)
  }
  const encryption = props.encryption
  if (encryption && typeof encryption === 'object' && !Array.isArray(encryption)) {
    picked.encryption = normalizeComparableValue(encryption)
  }
  const metrics = props.metrics
  if (metrics && typeof metrics === 'object' && !Array.isArray(metrics)) {
    picked.metrics = normalizeComparableValue(metrics)
  }
  return picked
}

function normalizedTags(value: unknown): Record<string, string> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  const tags = value as Record<string, unknown>
  const entries = Object.entries(tags)
    .filter((entry): entry is [string, string | number] =>
      typeof entry[1] === 'string' || typeof entry[1] === 'number')
    .map(([key, tagValue]): [string, string] => [key, String(tagValue)])
    .sort(([a], [b]) => a.localeCompare(b))
  return Object.fromEntries(entries)
}

function normalizeComparableValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(normalizeComparableValue)
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter((entry): entry is [string, unknown] => entry[1] !== undefined)
      .map(([key, entry]): [string, unknown] => [key, normalizeComparableValue(entry)])
      .sort(([a], [b]) => a.localeCompare(b))
    return Object.fromEntries(entries)
  }
  return value
}

function propsOf(node: PersistedNode): Record<string, unknown> {
  return node.props && typeof node.props === 'object' && !Array.isArray(node.props)
    ? node.props as Record<string, unknown>
    : {}
}

function stringProp(
  props: Record<string, unknown>,
  keys: readonly string[],
): string | null {
  for (const key of keys) {
    const value = props[key]
    if (typeof value === 'string' && value.trim().length > 0) return value.trim()
    if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  }
  return null
}

function parseResourceFingerprint(
  fallbackKey: string,
  value: unknown,
): NetworkReviewBaselineResourceFingerprint | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const raw = value as Record<string, unknown>
  const fingerprint = stringValue(raw.fingerprint)
  if (!fingerprint) return null
  const key = stringValue(raw.key) ?? fallbackKey
  return {
    key,
    nodeId: stringValue(raw.nodeId) ?? '',
    label: stringValue(raw.label) ?? key,
    type: stringValue(raw.type) ?? 'resource',
    provider: nullableStringValue(raw.provider),
    accountId: nullableStringValue(raw.accountId),
    region: nullableStringValue(raw.region),
    resourceType: nullableStringValue(raw.resourceType),
    fingerprint,
  }
}

function sourceRunIdsForBaseline(runs: readonly KnowledgeRun[]): string[] {
  return runs
    .filter((run) => run.status !== 'failed' && run.status !== 'cancelled')
    .map((run) => run.id)
    .filter((id, index, ids) => ids.indexOf(id) === index)
    .sort()
}

function arrayOfStrings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
    : []
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null
}

function nullableStringValue(value: unknown): string | null {
  return stringValue(value)
}

function numberValue(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

function dateLikeToIso(value: Date | string): string {
  return value instanceof Date ? value.toISOString() : value
}

function normalizeKeyPart(value: string | null | undefined): string {
  return String(value ?? 'unknown').toLowerCase().trim().replace(/\s+/g, '-')
}

function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value)
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, entry]) => entry !== undefined)
    .sort(([a], [b]) => a.localeCompare(b))
  return `{${entries.map(([key, entry]) => `${JSON.stringify(key)}:${stableStringify(entry)}`).join(',')}}`
}

function hashString(value: string): string {
  let hash = 2166136261
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return (hash >>> 0).toString(16).padStart(8, '0')
}
